import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTeamId } from "@/hooks/use-team-id";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function applyPrimaryColor(hex: string | null) {
  const el = document.documentElement;
  if (!hex) {
    el.style.removeProperty("--primary");
    el.style.removeProperty("--ring");
    el.style.removeProperty("--primary-light");
    return;
  }

  const hsl = hexToHsl(hex);
  if (!hsl) return;

  const { h, s, l } = hsl;
  el.style.setProperty("--primary", `${h} ${s}% ${l}%`);
  el.style.setProperty("--ring", `${h} ${s}% ${l}%`);
  el.style.setProperty("--primary-light", `${h} ${Math.max(s - 20, 30)}% 90%`);
}

export function useAppTheme() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [localColor, setLocalColor] = useState<string | null>(null);

  const { data: savedColor, isLoading } = useQuery({
    queryKey: ["app-theme", teamId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("teams")
        .select("options")
        .eq("id", teamId!)
        .single();
      if (error) throw error;
      const options = (data?.options as Record<string, unknown>) ?? {};
      const appTheme = (options.app_theme as Record<string, string>) ?? {};
      return appTheme.primary_color ?? null;
    },
    enabled: !!teamId,
    staleTime: Infinity,
  });

  const saveMutation = useMutation({
    mutationFn: async (hex: string | null) => {
      if (!teamId) throw new Error("No team");

      const { data: teamData } = await supabase
        .from("teams")
        .select("options")
        .eq("id", teamId)
        .single();

      const existingOptions = (teamData?.options as Record<string, unknown>) ?? {};
      const newOptions: Json = {
        ...existingOptions,
        app_theme: { primary_color: hex },
      };

      const { error } = await supabase
        .from("teams")
        .update({ options: newOptions })
        .eq("id", teamId);

      if (error) throw error;
      return hex;
    },
    onSuccess: (hex) => {
      // Update cache synchronously to avoid a flash to the previous color
      queryClient.setQueryData(["app-theme", teamId], hex);
    },
  });

  // The active color is localColor (for live preview) or savedColor
  const activeColor = localColor ?? savedColor ?? null;

  // Apply the color to CSS vars whenever it changes.
  // No cleanup that nullifies — multiple useAppTheme instances share the same
  // CSS vars on documentElement, so a per-instance unmount cleanup would wipe
  // colors set by other still-mounted instances (e.g. AppearanceSettings
  // unmounting on navigation would clear ProtectedLayout's applied color).
  useEffect(() => {
    applyPrimaryColor(activeColor);
  }, [activeColor]);

  const setPreviewColor = (hex: string | null) => {
    setLocalColor(hex);
  };

  const saveColor = async (hex: string | null) => {
    await saveMutation.mutateAsync(hex);
    setLocalColor(null); // Reset local override, let saved value take over
  };

  return {
    primaryColor: activeColor,
    setPreviewColor,
    saveColor,
    isSaving: saveMutation.isPending,
    isLoading,
  };
}
